import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { PawPrint, LayoutDashboard, Box, BarChart3, Calendar, LogOut, Plus, LogIn, ClipboardList, Users, History, Pill, ChevronDown, Home, Shield, Package, KeyRound, X, ClipboardCheck, Download, Menu, Stethoscope, ExternalLink } from 'lucide-react';
import axios from 'axios';
import { getInstallPromptAvailable, onInstallPromptChange, triggerInstall, isAppInstalled } from '../utils/pwa';
import PasswordInput from './PasswordInput';

function GestaoDropdown({ isActiveTarefas, isActiveGestao, isAdmin, tarefasPendentes, onNav }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const isActive = isActiveTarefas || isActiveGestao;
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);
  return (
    <div ref={ref} className="relative">
      <button onClick={() => setOpen(v => !v)} data-testid="nav-gestao-dropdown"
        className={`relative flex items-center gap-1 px-2.5 py-1.5 rounded-full text-sm font-medium transition-all whitespace-nowrap ${isActive ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground hover:bg-muted'}`}>
        <Users className="w-4 h-4" />Gestão <ChevronDown className="w-3.5 h-3.5" />
        {tarefasPendentes ? (
          <span className="absolute -top-1 -right-1 bg-red-600 text-white text-[10px] font-bold rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center" data-testid="nav-gestao-dropdown-badge">
            {tarefasPendentes > 99 ? '99+' : tarefasPendentes}
          </span>
        ) : null}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-56 bg-card border border-border rounded-xl shadow-xl overflow-hidden z-50" data-testid="gestao-dropdown-menu">
          <button onClick={() => { setOpen(false); onNav('/tarefas'); }} data-testid="dropdown-tarefas"
            className={`w-full text-left px-4 py-3 hover:bg-muted transition-colors flex items-center gap-3 border-b border-border ${isActiveTarefas ? 'text-primary' : ''}`}>
            <ClipboardCheck className="w-4 h-4 text-primary" />
            <div className="flex-1">
              <p className="text-sm font-medium">Tarefas</p>
              <p className="text-xs text-muted-foreground">Atribuir e acompanhar</p>
            </div>
            {tarefasPendentes ? (
              <span className="bg-red-600 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center">
                {tarefasPendentes > 99 ? '99+' : tarefasPendentes}
              </span>
            ) : null}
          </button>
          <button onClick={() => { setOpen(false); onNav('/gestao'); }} data-testid="dropdown-gestao"
            className={`w-full text-left px-4 py-3 hover:bg-muted transition-colors flex items-center gap-3 ${isActiveGestao ? 'text-primary' : ''}`}>
            <Users className="w-4 h-4 text-primary" />
            <div>
              <p className="text-sm font-medium">Utilizadores</p>
              <p className="text-xs text-muted-foreground">Gerir contas e permissões</p>
            </div>
          </button>
          <a href="https://www.animalog-ced.pt" target="_blank" rel="noopener noreferrer" data-testid="dropdown-programa-ced"
            onClick={() => setOpen(false)}
            className="w-full text-left px-4 py-3 hover:bg-muted transition-colors flex items-center gap-3 border-t border-border">
            <ExternalLink className="w-4 h-4 text-primary" />
            <div>
              <p className="text-sm font-medium">Programa CED</p>
              <p className="text-xs text-muted-foreground">animalog-ced.pt</p>
            </div>
          </a>
          {isAdmin && (
            <button onClick={() => { setOpen(false); onNav('/auditoria'); }} data-testid="dropdown-auditoria"
              className="w-full text-left px-4 py-3 hover:bg-muted transition-colors flex items-center gap-3 border-t border-border">
              <History className="w-4 h-4 text-primary" />
              <div>
                <p className="text-sm font-medium">Auditoria</p>
                <p className="text-xs text-muted-foreground">Quem fez o quê na aplicação</p>
              </div>
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const handleLogout = () => { logout(); navigate('/'); };
  const isActive = (p) => location.pathname === p;
  const isOp = user?.role === 'operacional';
  const canManage = user?.role === 'tecnico' || user?.role === 'admin';

  const [addOpen, setAddOpen] = useState(false);
  const [showChangePass, setShowChangePass] = useState(false);
  const [mobileMenu, setMobileMenu] = useState(false);
  const [tarefasPendentes, setTarefasPendentes] = useState(0);
  const [installAvail, setInstallAvail] = useState(getInstallPromptAvailable());
  const addRef = useRef(null);
  useEffect(() => {
    const onDoc = (e) => { if (addRef.current && !addRef.current.contains(e.target)) setAddOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  useEffect(() => {
    if (!user) return;
    const carregar = async () => {
      try {
        const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
        const r = await axios.get(`${API}/tarefas/contadores`);
        setTarefasPendentes((r.data?.pendente || 0) + (r.data?.em_curso || 0));
      } catch (_) { /* ignore */ }
    };
    carregar();
    const iv = setInterval(carregar, 60000);
    return () => clearInterval(iv);
  }, [user]);

  useEffect(() => onInstallPromptChange(setInstallAvail), []);
  const showInstall = installAvail && !isAppInstalled();

  const handleInstall = async () => {
    await triggerInstall();
  };

  return (
    <header data-testid="main-header" className={`sticky top-0 z-50 ${user ? 'bg-background/80 backdrop-blur-xl border-b border-border' : 'bg-background/60 backdrop-blur-sm'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`flex items-center justify-between ${user ? 'h-16' : 'h-14'}`}>
          <Link to="/" className="flex items-center gap-2 flex-shrink-0" data-testid="logo-link">
            {user ? (
              <>
                <img src="/logo.jpg" alt="AnimaLog" className="h-9 w-auto rounded-lg" onError={e => { e.target.style.display = 'none'; }} />
                <span className="font-heading font-black text-lg text-foreground">AnimaLog</span>
              </>
            ) : (
              <span data-testid="public-header-title" className="text-xs sm:text-base text-foreground font-medium tracking-tight leading-tight max-w-[200px] sm:max-w-none">Centro de Recolha Oficial de Vila Franca de Xira</span>
            )}
          </Link>
          <nav className="hidden lg:flex items-center gap-1">
            {user ? (
              <>
                <NavLink to="/" active={isActive('/')} testid="nav-animais"><LayoutDashboard className="w-4 h-4" />Animais</NavLink>
                {!isOp && <NavLink to="/historico" active={isActive('/historico')} testid="nav-historico"><History className="w-4 h-4" />Histórico</NavLink>}
                <NavLink to="/boxes" active={isActive('/boxes')} testid="nav-boxes"><Box className="w-4 h-4" />Boxes</NavLink>
                {!isOp && <NavLink to="/estatisticas" active={isActive('/estatisticas')} testid="nav-stats"><BarChart3 className="w-4 h-4" />Estatísticas</NavLink>}
                {!isOp && <NavLink to="/calendario" active={isActive('/calendario')} testid="nav-calendar"><Calendar className="w-4 h-4" />Calendário</NavLink>}
                {!isOp && <NavLink to="/stock" active={isActive('/stock')} testid="nav-stock"><Package className="w-4 h-4" />Stock</NavLink>}
                {!isOp && <NavLink to="/tratamentos" active={isActive('/tratamentos')} testid="nav-tratamentos"><Pill className="w-4 h-4" />Tratamentos</NavLink>}
                {!isOp && <NavLink to="/pedidos" active={isActive('/pedidos')} testid="nav-pedidos"><ClipboardList className="w-4 h-4" />Pedidos</NavLink>}
                {canManage ? (
                  <GestaoDropdown
                    isActiveTarefas={isActive('/tarefas')}
                    isActiveGestao={isActive('/gestao')}
                    isAdmin={user?.role === 'admin'}
                    tarefasPendentes={tarefasPendentes}
                    onNav={navigate}
                  />
                ) : (
                  <NavLink to="/tarefas" active={isActive('/tarefas')} testid="nav-tarefas" badge={tarefasPendentes}>
                    <ClipboardCheck className="w-4 h-4" />Tarefas
                  </NavLink>
                )}

                <div ref={addRef} className="relative ml-1">
                  <button onClick={() => setAddOpen(v => !v)} data-testid="nav-add-animal"
                    className="flex items-center gap-1 bg-primary text-primary-foreground rounded-full px-3 py-1.5 text-sm font-medium hover:brightness-110 transition-all">
                    <Plus className="w-4 h-4" />Adicionar <ChevronDown className="w-3.5 h-3.5" />
                  </button>
                  {addOpen && (
                    <div className="absolute right-0 mt-2 w-60 bg-card border border-border rounded-xl shadow-xl overflow-hidden z-50" data-testid="add-dropdown">
                      <button onClick={() => { setAddOpen(false); navigate('/adicionar'); }} data-testid="add-cro"
                        className={`w-full text-left px-4 py-3 hover:bg-muted transition-colors flex items-start gap-3 ${!isOp ? 'border-b border-border' : ''}`}>
                        <Home className="w-4 h-4 text-primary mt-0.5" />
                        <div><p className="text-sm font-medium">Entrada no CRO</p><p className="text-xs text-muted-foreground">Animal recolhido no canil</p></div>
                      </button>
                      {!isOp && (
                      <button onClick={() => { setAddOpen(false); navigate('/adicionar/sequestro'); }} data-testid="add-sequestro"
                        className="w-full text-left px-4 py-3 hover:bg-muted transition-colors flex items-start gap-3 border-b border-border">
                        <Shield className="w-4 h-4 text-destructive mt-0.5" />
                        <div><p className="text-sm font-medium">Sequestro Domiciliário</p><p className="text-xs text-muted-foreground">Animal em sequestro em casa do titular</p></div>
                      </button>
                      )}
                      {!isOp && (
                      <button onClick={() => { setAddOpen(false); navigate('/tratamentos-externos?new=1'); }} data-testid="add-tratamento-externo"
                        className="w-full text-left px-4 py-3 hover:bg-muted transition-colors flex items-start gap-3">
                        <Stethoscope className="w-4 h-4 text-teal-600 mt-0.5" />
                        <div><p className="text-sm font-medium">Tratamento Externo</p><p className="text-xs text-muted-foreground">Animal de fora do CRO que vem fazer tratamento</p></div>
                      </button>
                      )}
                    </div>
                  )}
                </div>

                <button onClick={() => setShowChangePass(true)} title="Alterar a minha password" data-testid="change-password-btn"
                  className="ml-1 text-muted-foreground hover:text-foreground transition-colors px-2 py-2">
                  <KeyRound className="w-4 h-4" />
                </button>
                {showInstall && (
                  <button onClick={handleInstall} title="Instalar como aplicação" data-testid="install-pwa-btn"
                    className="ml-1 text-muted-foreground hover:text-foreground transition-colors px-2 py-2">
                    <Download className="w-4 h-4" />
                  </button>
                )}
                <button onClick={handleLogout} data-testid="logout-btn" className="ml-1 text-muted-foreground hover:text-foreground transition-colors px-2 py-2"><LogOut className="w-4 h-4" /></button>
              </>
            ) : (
              <Link to="/login" data-testid="nav-login" className="flex items-center gap-1 bg-primary text-primary-foreground rounded-full px-4 py-2 text-sm font-medium hover:brightness-110 transition-all"><LogIn className="w-4 h-4" />Entrar</Link>
            )}
          </nav>

          {/* Mobile login button (visible only when NOT logged in) */}
          {!user && (
            <Link to="/login" data-testid="mobile-nav-login"
              className="lg:hidden flex items-center gap-1 bg-primary text-primary-foreground rounded-full px-4 py-2 text-sm font-medium hover:brightness-110 transition-all">
              <LogIn className="w-4 h-4" />Entrar
            </Link>
          )}

          {/* Mobile hamburger trigger — only when user is logged in */}
          {user && (
            <button onClick={() => setMobileMenu(true)} data-testid="mobile-menu-toggle"
              className="lg:hidden relative ml-2 p-2 rounded-full text-foreground hover:bg-muted transition-colors">
              <Menu className="w-6 h-6" />
              {tarefasPendentes ? (
                <span className="absolute top-0.5 right-0.5 bg-red-600 text-white text-[10px] font-bold rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center">
                  {tarefasPendentes > 99 ? '99+' : tarefasPendentes}
                </span>
              ) : null}
            </button>
          )}
        </div>
      </div>
      {showChangePass && <ChangePasswordModal onClose={() => setShowChangePass(false)} />}
      {mobileMenu && user && (
        <MobileMenu
          user={user}
          isOp={isOp}
          canManage={canManage}
          tarefasPendentes={tarefasPendentes}
          showInstall={showInstall}
          onInstall={handleInstall}
          onChangePass={() => { setMobileMenu(false); setShowChangePass(true); }}
          onLogout={() => { setMobileMenu(false); handleLogout(); }}
          onClose={() => setMobileMenu(false)}
          onNav={(to) => { setMobileMenu(false); navigate(to); }}
          currentPath={location.pathname}
        />
      )}
    </header>
  );
}

function ChangePasswordModal({ onClose }) {
  const [nova, setNova] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
  const submit = async (e) => {
    e.preventDefault();
    if (nova.length < 6) { alert('A nova password deve ter pelo menos 6 caracteres'); return; }
    if (nova !== confirm) { alert('A nova password e a confirmação não coincidem'); return; }
    setLoading(true);
    try {
      await axios.put(`${API}/auth/alterar-password`, { password_nova: nova });
      alert('Password alterada com sucesso! Vais ter de fazer login novamente na próxima vez.');
      onClose();
    } catch (err) {
      alert(err.response?.data?.detail || 'Erro ao alterar password');
    } finally { setLoading(false); }
  };
  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <form onSubmit={submit} className="bg-card rounded-2xl border border-border shadow-2xl w-full max-w-sm p-5 space-y-3 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-heading font-bold text-lg flex items-center gap-2"><KeyRound className="w-4 h-4 text-primary" /> Alterar Password</h3>
          <button type="button" onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div>
          <label className="text-xs font-medium block mb-1">Nova password (mínimo 6 caracteres)</label>
          <PasswordInput value={nova} onChange={e => setNova(e.target.value)} required testid="cp-nova" autoFocus />
        </div>
        <div>
          <label className="text-xs font-medium block mb-1">Confirmar nova password</label>
          <PasswordInput value={confirm} onChange={e => setConfirm(e.target.value)} required testid="cp-confirm" />
        </div>
        <div className="flex gap-2 justify-end pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-full bg-muted text-foreground text-sm">Cancelar</button>
          <button type="submit" disabled={loading} data-testid="cp-submit"
            className="px-4 py-2 rounded-full bg-primary text-primary-foreground text-sm font-medium disabled:opacity-50">
            {loading ? 'A guardar…' : 'Alterar'}
          </button>
        </div>
      </form>
    </div>,
    document.body
  );
}

function NavLink({ to, active, children, testid, badge }) {
  return (
    <Link to={to} data-testid={testid} className={`relative flex items-center gap-1 px-2.5 py-1.5 rounded-full text-sm font-medium transition-all whitespace-nowrap ${active ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground hover:bg-muted'}`}>
      {children}
      {badge ? (
        <span className="absolute -top-1 -right-1 bg-red-600 text-white text-[10px] font-bold rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center" data-testid={`${testid}-badge`}>
          {badge > 99 ? '99+' : badge}
        </span>
      ) : null}
    </Link>
  );
}



function MobileMenu({ user, isOp, canManage, tarefasPendentes, showInstall, onInstall, onChangePass, onLogout, onClose, onNav, currentPath }) {
  // Lock scroll while open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const isActive = (p) => currentPath === p;
  const items = [
    { to: '/', label: 'Animais', icon: LayoutDashboard, show: true },
    { to: '/historico', label: 'Histórico', icon: History, show: !isOp },
    { to: '/boxes', label: 'Boxes', icon: Box, show: true },
    { to: '/tarefas', label: 'Tarefas', icon: ClipboardCheck, show: true, badge: tarefasPendentes },
    { to: '/estatisticas', label: 'Estatísticas', icon: BarChart3, show: !isOp },
    { to: '/calendario', label: 'Calendário', icon: Calendar, show: !isOp },
    { to: '/stock', label: 'Stock', icon: Package, show: !isOp },
    { to: '/tratamentos', label: 'Tratamentos', icon: Pill, show: !isOp },
    { to: '/pedidos', label: 'Pedidos', icon: ClipboardList, show: !isOp },
    { to: '/gestao', label: 'Utilizadores', icon: Users, show: canManage },
  ].filter(i => i.show);

  // Use a portal so the drawer escapes any backdrop-filter ancestor that
  // would otherwise turn it into a containing block for fixed positioning.
  return createPortal(
    <div className="fixed inset-0 z-[100] lg:hidden bg-black/50 backdrop-blur-sm" onClick={onClose} data-testid="mobile-menu">
      <div onClick={e => e.stopPropagation()} className="absolute inset-y-0 right-0 w-[88%] max-w-sm bg-card shadow-2xl flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div>
            <p className="font-heading font-bold text-base">{user.nome || user.email}</p>
            <p className="text-xs text-muted-foreground capitalize">{user.role}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-muted" data-testid="mobile-menu-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto p-3 space-y-1">
          {items.map(it => {
            const Ico = it.icon;
            return (
              <button key={it.to} onClick={() => onNav(it.to)} data-testid={`mobile-nav-${it.to.replace('/','') || 'home'}`}
                className={`w-full text-left flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-medium transition-colors ${isActive(it.to) ? 'bg-primary/10 text-primary' : 'text-foreground hover:bg-muted'}`}>
                <Ico className="w-5 h-5" />
                <span className="flex-1">{it.label}</span>
                {it.badge ? (
                  <span className="bg-red-600 text-white text-[11px] font-bold rounded-full min-w-[20px] h-5 px-1.5 flex items-center justify-center">
                    {it.badge > 99 ? '99+' : it.badge}
                  </span>
                ) : null}
              </button>
            );
          })}
          <a href="https://www.animalog-ced.pt" target="_blank" rel="noopener noreferrer" data-testid="mobile-nav-programa-ced" onClick={onClose}
            className="w-full text-left flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-medium text-foreground hover:bg-muted">
            <ExternalLink className="w-5 h-5" />
            <span className="flex-1">Programa CED</span>
          </a>

          <div className="pt-3 mt-3 border-t border-border space-y-1">
            <button onClick={() => onNav('/adicionar')} data-testid="mobile-nav-adicionar"
              className="w-full text-left flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-medium bg-primary text-primary-foreground">
              <Plus className="w-5 h-5" />
              <span className="flex-1">Adicionar animal</span>
            </button>
            {!isOp && (
              <button onClick={() => onNav('/adicionar/sequestro')}
                className="w-full text-left flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-medium text-foreground hover:bg-muted">
                <Shield className="w-5 h-5 text-destructive" />
                <span className="flex-1">Sequestro Domiciliário</span>
              </button>
            )}
            {!isOp && (
              <button onClick={() => onNav('/tratamentos-externos?new=1')} data-testid="mobile-add-trat-ext"
                className="w-full text-left flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-medium text-foreground hover:bg-muted">
                <Stethoscope className="w-5 h-5 text-teal-600" />
                <span className="flex-1">Tratamento Externo</span>
              </button>
            )}
          </div>
        </nav>

        <div className="border-t border-border p-3 space-y-1">
          {showInstall && (
            <button onClick={() => { onClose(); onInstall(); }} data-testid="mobile-install-pwa"
              className="w-full text-left flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-medium text-foreground hover:bg-muted">
              <Download className="w-5 h-5" /> Instalar como app
            </button>
          )}
          <button onClick={onChangePass} data-testid="mobile-change-password"
            className="w-full text-left flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-medium text-foreground hover:bg-muted">
            <KeyRound className="w-5 h-5" /> Alterar password
          </button>
          <button onClick={onLogout} data-testid="mobile-logout"
            className="w-full text-left flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-medium text-red-700 hover:bg-red-50">
            <LogOut className="w-5 h-5" /> Sair
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
